import { useEffect, useState } from 'react'
import {
  Globe,
  Users,
  DollarSign,
  Gift,
  TrendingUp,
  RefreshCw,
  Copy,
  Plus,
  Flag,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import countryManagerService from '@/services/countryManagerService'
import PageContainer from '@/components/common/PageContainer'
import KpiCard from '@/components/KpiCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import DataTable from '@/components/common/DataTable'
import { cn } from '@/lib/utils'

const CODE_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'available', label: 'Libres' },
  { value: 'used', label: 'Usados' },
  { value: 'expired', label: 'Expirados' },
]

const CODE_BG = {
  available: 'bg-surface-muted',
  used: 'bg-warning-50 dark:bg-warning-500/15',
  expired: 'bg-danger-50 dark:bg-danger-500/15',
}

const CODE_BADGE_VARIANT = {
  available: 'success',
  used: 'warning',
  expired: 'danger',
}

const CODE_LABEL = {
  available: 'Libre',
  used: 'Usado',
  expired: 'Expirado',
}

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(
    amount || 0
  )

export default function CountryDashboard() {
  const { partner } = useAuthStore()
  const [dashboard, setDashboard] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const [trialDialog, setTrialDialog] = useState(false)
  const [trialQuantity, setTrialQuantity] = useState(10)
  const [generatingCodes, setGeneratingCodes] = useState(false)
  const [generatedCodes, setGeneratedCodes] = useState([])
  const [trialCodesData, setTrialCodesData] = useState(null)
  const [codeFilter, setCodeFilter] = useState('all')

  const countryName = countryManagerService.getCountryName(partner?.managedCountry)

  const fetchDashboard = async () => {
    try {
      setError(null)
      const response = await countryManagerService.getCountryDashboard()
      setDashboard(response.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar dashboard')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTrialCodes = async () => {
    try {
      const response = await countryManagerService.getTrialCodesStats()
      setTrialCodesData(response.data)
    } catch {
      // silenciar si no hay permisos
    }
  }

  useEffect(() => {
    fetchDashboard()
    fetchTrialCodes()
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchDashboard()
    setRefreshing(false)
  }

  const handleGenerateCodes = async () => {
    setGeneratingCodes(true)
    try {
      const response = await countryManagerService.generateTrialCodes(trialQuantity)
      setGeneratedCodes(response.data?.codes || [])
      toast.success(`${trialQuantity} códigos generados`)
      fetchDashboard()
      fetchTrialCodes()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al generar códigos')
    } finally {
      setGeneratingCodes(false)
    }
  }

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
    toast.success('Código copiado')
  }

  const handleCopyAllCodes = () => {
    navigator.clipboard.writeText(generatedCodes.join('\n'))
    toast.success('Todos los códigos copiados')
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-72" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-72" />
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle size={16} />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={handleRefresh}>
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  const progress = dashboard?.progress || {}
  const metrics = dashboard?.metrics || {}
  const trialCodes = dashboard?.trialCodes || {}
  const countryPartners = dashboard?.countryPartners || []
  const remaining = trialCodes.remaining ?? (trialCodes.total || 0) - (trialCodes.distributed || 0)
  const allCodes = trialCodesData?.codes || []
  const counts = {
    all: allCodes.length,
    available: allCodes.filter((c) => c.status === 'available').length,
    used: allCodes.filter((c) => c.status === 'used').length,
    expired: allCodes.filter((c) => c.status === 'expired').length,
  }
  const filteredCodes =
    codeFilter === 'all' ? allCodes : allCodes.filter((c) => c.status === codeFilter)

  const kpis = [
    {
      title: 'Suscripciones país',
      value: metrics.totalReferrals || 0,
      icon: <Users size={20} />,
      tone: 'primary',
    },
    {
      title: 'Referidos activos',
      value: metrics.activeReferrals || 0,
      icon: <TrendingUp size={20} />,
      tone: 'success',
    },
    {
      title: 'Códigos distribuidos',
      value: `${trialCodes.distributed || 0}/${trialCodes.total || 0}`,
      icon: <Gift size={20} />,
      tone: 'accent',
    },
    {
      title: 'Comisión total (5%)',
      value: formatCurrency(metrics.totalCommissionsEarned),
      icon: <DollarSign size={20} />,
      tone: 'primary',
    },
  ]

  const columnasPartners = [
    {
      clave: 'partner', titulo: 'Partner', prioridad: 'titulo',
      celda: (p) => (
        <div className="min-w-0">
          <p className="font-medium text-foreground">{p.name}</p>
          {p.contact && <p className="text-caption text-subtle break-words">{p.contact}</p>}
        </div>
      ),
    },
    { clave: 'tipo', titulo: 'Tipo', prioridad: 'meta', celda: (p) => <Badge variant="outline">{p.type || p.tier || '—'}</Badge> },
    { clave: 'referidos', titulo: 'Referidos', alineacion: 'derecha', prioridad: 'meta', celda: (p) => <span className="tabular-nums">{p.referrals || 0}</span> },
    {
      clave: 'ingresos', titulo: 'Ingresos', alineacion: 'derecha', prioridad: 'valor',
      celda: (p) => <span className="tabular-nums font-medium text-foreground">{formatCurrency(p.revenue)}</span>,
    },
  ]

  return (
    <PageContainer>
      {/* Header con icono país + chip rol */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Globe size={28} className="text-accent" />
            <h2 className="font-display text-h1 font-semibold text-foreground leading-tight">
              {countryName}
            </h2>
            <Badge variant="accent">Gerente Comercial</Badge>
          </div>
          <p className="text-body-sm text-subtle mt-1">
            Gestión de tu país y equipo de partners
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          aria-label="Actualizar"
        >
          <RefreshCw size={16} className={cn(refreshing && 'animate-spin')} />
        </Button>
      </div>

      {/* Progress card */}
      <Card className="border-accent-200 dark:border-accent-800 bg-accent-50 dark:bg-accent-900/40">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Flag size={16} className="text-accent-700 dark:text-accent-300" />
              <p className="font-semibold text-foreground">Progreso hacia meta</p>
            </div>
            <p className="text-body-sm text-subtle">
              {progress.current || 0} / {progress.target || 100} suscripciones
              {progress.daysRemaining != null && ` · ${progress.daysRemaining} días`}
            </p>
          </div>
          <div className="h-3 rounded-full bg-accent-100 dark:bg-background/70 overflow-hidden">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${Math.min(progress.percentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-caption text-subtle mt-1.5">
            {(progress.percentage || 0).toFixed(1)}% completado
          </p>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k, i) => (
          <KpiCard key={i} {...k} />
        ))}
      </div>

      {/* Trial codes + Partners */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-h3">Códigos de prueba</CardTitle>
              <Badge variant="default">{remaining} disponibles</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(5.5rem,1fr))] gap-2">
              {[
                { label: 'Total', value: trialCodes.total || 0, tone: '' },
                { label: 'Usados', value: trialCodes.distributed || 0, tone: '' },
                {
                  label: 'Convertidos',
                  value: trialCodes.converted || 0,
                  tone: 'success',
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className={cn(
                    'min-w-0 rounded-md p-2 text-center',
                    s.tone === 'success' ? 'bg-success-50 dark:bg-success-500/15' : 'bg-surface-muted'
                  )}
                >
                  <p className="text-caption text-subtle">{s.label}</p>
                  <p
                    className={cn(
                      'font-display text-h3 font-semibold',
                      s.tone === 'success' ? 'text-success-700 dark:text-success-300' : 'text-foreground'
                    )}
                  >
                    {s.value}
                  </p>
                </div>
              ))}
            </div>

            <Button
              variant="accent"
              className="w-full"
              onClick={() => {
                setGeneratedCodes([])
                setTrialDialog(true)
              }}
            >
              <Plus size={16} /> Generar códigos
            </Button>

            {allCodes.length > 0 && (
              <>
                <Separator />
                <p className="text-body-sm font-medium text-subtle">
                  Códigos ({counts.all})
                </p>
                <div className="grid grid-cols-2 gap-1 rounded-md border border-line p-1 sm:grid-cols-4 lg:grid-cols-2">
                  {CODE_FILTERS.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setCodeFilter(f.value)}
                      className={cn(
                        'min-h-10 px-1 py-1 text-caption rounded transition-colors',
                        codeFilter === f.value
                          ? 'bg-primary text-primary-foreground font-semibold'
                          : 'text-subtle hover:bg-surface-muted dark:hover:bg-neutral-800'
                      )}
                    >
                      {f.label} ({counts[f.value]})
                    </button>
                  ))}
                </div>

                <div className="max-h-72 overflow-y-auto space-y-1">
                  {filteredCodes.length === 0 ? (
                    <p className="text-caption text-subtle text-center py-3">
                      No hay códigos con este estado
                    </p>
                  ) : (
                    filteredCodes.map((c, i) => (
                      <div
                        key={i}
                        className={cn(
                          'flex items-center gap-2 rounded-md py-1 pl-2 pr-1 min-h-10',
                          CODE_BG[c.status] || 'bg-surface-muted'
                        )}
                      >
                        <code className="min-w-0 flex-1 font-mono text-caption truncate">
                          {c.code}
                        </code>
                        <Badge
                          variant={CODE_BADGE_VARIANT[c.status] || 'secondary'}
                          className="text-micro"
                        >
                          {CODE_LABEL[c.status] || c.status}
                        </Badge>
                        {c.status === 'available' && (
                          <button
                            type="button"
                            onClick={() => handleCopyCode(c.code)}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-subtle hover:text-primary"
                            aria-label="Copiar"
                          >
                            <Copy size={12} />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-h3">Partners en {countryName}</CardTitle>
          </CardHeader>
          <CardContent>
            {countryPartners.length === 0 ? (
              <Alert variant="info">
                <AlertDescription>
                  Aún no hay partners registrados en {countryName}
                </AlertDescription>
              </Alert>
            ) : (
              <DataTable
                tarjetasEnMovil
                etiquetaTabla={`Partners en ${countryName}`}
                columnas={columnasPartners}
                filas={countryPartners}
                claveDeFila={(p, idx) => String(p.id || idx)}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={trialDialog} onOpenChange={setTrialDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generar códigos de prueba</DialogTitle>
          </DialogHeader>

          {generatedCodes.length === 0 ? (
            <>
              <Alert variant="info">
                <AlertDescription>
                  Los códigos permiten a abogados probar CoreDesk Pro por 7-14 días sin
                  costo.
                </AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={50}
                  value={trialQuantity}
                  onChange={(e) =>
                    setTrialQuantity(
                      Math.max(1, Math.min(50, Number(e.target.value)))
                    )
                  }
                />
                <p className="text-caption text-subtle">
                  Máximo 50 códigos por generación.
                </p>
              </div>
            </>
          ) : (
            <>
              <Alert variant="success">
                <AlertDescription>
                  {generatedCodes.length} códigos generados
                </AlertDescription>
              </Alert>
              <div className="max-h-72 overflow-y-auto space-y-1">
                {generatedCodes.map((code, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 rounded-md bg-surface-muted py-1 pl-2 pr-1"
                  >
                    <code className="min-w-0 flex-1 truncate font-mono text-body-sm">{code}</code>
                    <button
                      type="button"
                      onClick={() => handleCopyCode(code)}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded text-subtle hover:bg-card"
                      aria-label="Copiar"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={handleCopyAllCodes}>
                <Copy size={16} /> Copiar todos
              </Button>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialog(false)}>
              Cerrar
            </Button>
            {generatedCodes.length === 0 && (
              <Button
                variant="accent"
                onClick={handleGenerateCodes}
                disabled={generatingCodes}
              >
                {generatingCodes ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Generando…
                  </>
                ) : (
                  'Generar'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
